import { useCallback, useMemo, useReducer, useRef } from 'react';

/**
 * 심사 화면이 "관리자가 본 버전"을 기억한다 — 승인·반려가 보내는 seenUpdatedAt.
 *
 * 목록은 관리자가 부르지 않아도 다시 불러와진다 (탭 복귀 때 토큰 갱신 → 세션이 바뀌어 load 재실행).
 * 그때 받은 새 updatedAt 을 그대로 보내면, 판매자가 철회 → 수정 → 다시 요청한 내용을
 * 관리자가 보지도 않고 승인하게 된다. 그래서:
 *  - 관리자가 직접 부른 목록(처음 열기·필터·검색)의 행만 "본 것"으로 기억한다 (baseline)
 *  - 그 밖의 다시 불러오기에서 바뀌거나 새로 나타난 행은 기억을 바꾸지 않는다 → isStale
 *  - 관리자가 [바뀐 내용 확인]을 누르거나 그 행을 직접 처리한 뒤에만 지금 버전으로 바꾼다 (acknowledge)
 * 서버는 받은 seenUpdatedAt 을 지금 버전과 비교한다 (assertSeenVersion) — 화면이 본 적 없는 버전은 통과하지 못한다.
 */
export function useSeenVersions() {
  const seen = useRef(new Map<string, string>());
  const [revision, bump] = useReducer((x: number) => x + 1, 0);

  /** baseline=true 면 처음 보는 행을 본 것으로 기억, false 면 아무것도 바꾸지 않는다 */
  const observe = useCallback((entries: Array<[string, string]>, baseline: boolean) => {
    if (!baseline) return;
    let changed = false;
    for (const [key, version] of entries) {
      if (!seen.current.has(key)) {
        seen.current.set(key, version);
        changed = true;
      }
    }
    if (changed) bump();
  }, []);

  const acknowledge = useCallback((key: string, version: string) => {
    seen.current.set(key, version);
    bump();
  }, []);

  return useMemo(
    () => ({
      observe,
      acknowledge,
      /** 화면이 보여 준 적 없는 버전 — 새로 나타났거나 그사이 바뀜 */
      isStale: (key: string, version: string) => seen.current.get(key) !== version,
      /** 처음 보는 행인지 (새로 들어옴) / 본 뒤에 바뀐 행인지 */
      isNew: (key: string) => !seen.current.has(key),
      /** 서버에 보낼 본 버전 — 확인 전이면 예전 버전이라 서버가 409 로 막는다 */
      seenOf: (key: string, fallback: string) => seen.current.get(key) ?? fallback,
      revision,
    }),
    [observe, acknowledge, revision],
  );
}
